import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, EyeOff, ArrowRight, Check, ArrowLeft } from "lucide-react";
import { Envelope, Lock, User, Phone, Gift, Lightning, Leaf } from "@phosphor-icons/react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

// Auth illustrations from Nano Banana 2
const AUTH_IMAGES = {
  login: "/auth/auth-welcome.png",
  register: "/auth/auth-register.png",
  verify: "/auth/auth-verify.png",
  success: "/auth/auth-success.png",
};

const InputField = ({ icon: Icon, label, type: initialType, showToggle, error, name, ...props }) => {
  const [showPw, setShowPw] = useState(false);
  const type = showToggle ? (showPw ? "text" : "password") : initialType;
  const [focused, setFocused] = useState(false);
  const inputId = `auth-input-${name}`;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId}
          className="block text-sm font-medium"
          style={{ color: C.textMuted, fontFamily: BRAND.typography.body }}>
          {label}
        </label>
      )}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center rounded-l-2xl transition-colors"
          style={{ backgroundColor: focused ? C.green + "15" : C.green + "08" }}>
          <Icon size={20} weight="duotone" style={{ color: focused ? C.green : C.textMuted }} />
        </div>
        <input {...props} id={inputId} name={name} type={type}
          className="w-full h-14 rounded-2xl text-base font-medium outline-none transition-all"
          style={{
            paddingLeft: "3.2rem",
            paddingRight: showToggle ? "3rem" : "1rem",
            backgroundColor: C.bgCard,
            border: `2px solid ${error ? C.error : focused ? C.green : C.border}`,
            color: C.text,
            fontFamily: BRAND.typography.body,
            fontSize: "16px",
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
        />
        {showToggle && (
          <button type="button" onClick={() => setShowPw(!showPw)}
            aria-label={showPw ? "Passwort verbergen" : "Passwort anzeigen"}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 transition-colors hover:opacity-70"
            style={{ color: C.textMuted }}>
            {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
      </div>
      {error && (
        <p id={`${name}-error`} className="text-xs font-medium" style={{ color: C.error }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

const OTPInput = ({ length = 6, onComplete, onChange }) => {
  const [values, setValues] = useState(Array(length).fill(""));
  const refs = useRef([]);

  const handleChange = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...values];
    next[i] = v;
    setValues(next);
    if (v && i < length - 1) refs.current[i + 1]?.focus();
    if (onChange) onChange(next.join(""));
    if (next.every(d => d) && next.join("").length === length) onComplete(next.join(""));
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !values[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    const next = [...values];
    pasted.split("").forEach((d, i) => { next[i] = d; });
    setValues(next);
    if (next.every(d => d)) onComplete(next.join(""));
  };

  useEffect(() => { refs.current[0]?.focus(); }, []);

  return (
    <div className="flex gap-2 sm:gap-3 justify-center" onPaste={handlePaste}>
      {values.map((v, i) => (
        <input key={i} ref={el => refs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1} value={v}
          aria-label={`Ziffer ${i + 1} von ${length}`}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold rounded-2xl outline-none transition-all"
          style={{
            backgroundColor: C.bgCard,
            border: `2px solid ${v ? C.green : C.border}`,
            color: C.green,
            fontFamily: BRAND.typography.heading,
          }}
        />
      ))}
    </div>
  );
};

export default function AuthModal({ isOpen, onClose }) {
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verified, setVerified] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", password: "" });

  const { login, register } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === "Escape" && mode !== "verify") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail === "login" || e.detail === "register") setMode(e.detail);
    };
    document.addEventListener("set-auth-mode", handler);
    return () => document.removeEventListener("set-auth-mode", handler);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const sendVerificationCode = useCallback(async (token) => {
    try {
      // Send verification code via email
      await fetch(`${API}/api/auth/send-verification`, {
        method: "POST", headers: { "Authorization": `Bearer ${token}` },
      });
      setResendCooldown(60);
    } catch (e) {
      toast.error("Verifizierungscode konnte nicht gesendet werden. Versuch es gleich nochmal.");
    }
  }, [API]);

  const handleVerifyCode = async (code) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${verifyToken}` },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (res.ok) {
        setVerified(true);
        toast.success("Willkommen in der Farm-Familie!");
        setTimeout(() => onClose(), 2000);
      } else {
        toast.error("Hmm, der Code passt nicht. Probier's nochmal!");
      }
    } catch { toast.error("Da hat etwas nicht geklappt. Versuch's gleich nochmal."); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) { toast.error("Vergiss nicht deine E-Mail einzutragen."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await res.json();
      if (res.ok) {
        setResetSent(true);
        toast.success("Neues Passwort ist unterwegs zu deinem Postfach!");
      } else {
        toast.error(data.detail || "Da ist etwas schiefgegangen. Probier es nochmal.");
      }
    } catch { toast.error("Ups, da ging was schief. Probier es gleich nochmal."); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const result = await login(formData.email, formData.password);
        if (result && result.verified === false) {
          const token = localStorage.getItem("token");
          if (token) { setVerifyEmail(formData.email); setVerifyToken(token); setMode("verify"); sendVerificationCode(token); return; }
        }
        onClose();
      } else {
        // Check if promo registration (matcha-gratis link)
        const isPromo = localStorage.getItem("frankysfarm_promo") === "matcha-gratis";
        if (isPromo) {
          formData.google_review = true;
          localStorage.removeItem("frankysfarm_promo");
          localStorage.removeItem("frankysfarm_google_review");
        }
        await register(formData);
        const token = localStorage.getItem("token");
        if (token) { setVerifyEmail(formData.email); setVerifyToken(token); setMode("verify"); sendVerificationCode(token); }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "";
      if (msg.includes("already") || msg.includes("existiert")) {
        toast.error("Diese E-Mail gibt es schon. Vielleicht willst du dich anmelden?");
      } else if (msg.includes("passwort") || msg.includes("password") || msg.includes("falsch")) {
        toast.error("E-Mail oder Passwort stimmt nicht. Nochmal versuchen?");
      } else {
        toast.error(msg || "Da ist etwas schiefgegangen. Probier es nochmal.");
      }
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;
  const isLogin = mode === "login";
  const isForgot = mode === "forgot";
  const isVerify = mode === "verify";

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        style={{ backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)" }}
        onClick={isVerify ? undefined : onClose}>

        <motion.div
          role="dialog" aria-modal="true" aria-labelledby="auth-modal-title"
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 30, stiffness: 400 }}
          className="w-full max-w-[420px] rounded-t-[28px] sm:rounded-[28px] overflow-hidden shadow-2xl flex flex-col relative"
          style={{ backgroundColor: C.bgCard, maxHeight: "94vh" }}
          onClick={(e) => e.stopPropagation()}>

          {/* Drag handle (mobile) */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 rounded-full" style={{ backgroundColor: C.border }} />
          </div>

          {/* Header accent bar */}
          <div style={{ height: 4, background: `linear-gradient(90deg, ${C.green}, ${C.orange})` }} />

          {/* Close */}
          {!isVerify && (
            <motion.button whileTap={{ scale: 0.9 }} onClick={onClose}
              aria-label="Dialog schliessen"
              className="absolute top-5 right-5 w-10 h-10 rounded-full flex items-center justify-center z-10 transition-colors hover:opacity-80"
              style={{ backgroundColor: C.bg }}>
              <X size={18} style={{ color: C.textMuted }} />
            </motion.button>
          )}

          {/* Content */}
          <div className="overflow-y-auto flex-1 px-6 pt-6 pb-8" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom, 0px))" }}>

            <AnimatePresence mode="wait">
              {/* ===== VERIFY ===== */}
              {isVerify && (
                <motion.div key="verify" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                  className="space-y-6">
                  {verified ? (
                    <div className="text-center py-4">
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                        transition={{ type: "spring", damping: 15, stiffness: 200 }}>
                        <img src={AUTH_IMAGES.success} alt="Willkommen!" className="w-40 h-40 mx-auto object-contain mb-4" />
                      </motion.div>
                      <h2 id="auth-modal-title" className="text-xl font-bold"
                        style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                        Willkommen in der Familie!
                      </h2>
                      <p className="text-sm mt-2 leading-relaxed" style={{ color: C.textMuted }}>
                        Von einem kleinen Cafe in Aachen zu einer Community die zusammen waechst.
                        Wir freuen uns riesig, dass du dabei bist!
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Illustration */}
                      <div className="flex justify-center">
                        <img src={AUTH_IMAGES.verify} alt="E-Mail bestätigen"
                          className="w-32 h-32 object-contain" />
                      </div>

                      <div className="text-center">
                        <h2 id="auth-modal-title" className="text-2xl font-bold"
                          style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                          Bestätige deine E-Mail
                        </h2>
                        <p className="text-sm mt-2" style={{ color: C.textMuted }}>
                          Wir haben dir einen Code per E-Mail geschickt an <strong style={{ color: C.text }}>{verifyEmail.includes("@") ? verifyEmail : "dein Handy"}</strong>
                        </p>
                        <p className="text-xs mt-1" style={{ color: C.textMuted }}>
                          Schau auch im Spam-Ordner nach
                        </p>
                      </div>

                      <OTPInput onComplete={(code) => { setOtpValue(code); handleVerifyCode(code); }} onChange={(val) => setOtpValue(val)} />

                      {/* Manual submit button */}
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => { if (otpValue.length === 6) handleVerifyCode(otpValue); }}
                        disabled={loading || otpValue.length < 6}
                        className="w-full py-4 rounded-2xl font-bold text-white text-base mt-4"
                        style={{ backgroundColor: otpValue.length === 6 ? C.green : (C.border || "#E6DED3") }}>
                        {loading ? "Wird geprüft..." : "Code bestätigen"}
                      </motion.button>

                      <div className="text-center space-y-3">
                        <button onClick={() => { if (resendCooldown <= 0) sendVerificationCode(verifyToken); }}
                          disabled={resendCooldown > 0} className="text-sm font-semibold transition-colors"
                          style={{ color: resendCooldown > 0 ? C.textMuted : C.green }}>
                          {resendCooldown > 0 ? `Erneut senden in ${resendCooldown}s` : "Code erneut senden"}
                        </button>

                      </div>
                    </>
                  )}
                </motion.div>
              )}

              {/* ===== FORGOT ===== */}
              {isForgot && (
                <motion.div key="forgot" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  {resetSent ? (
                    <div className="text-center space-y-5 py-4">
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                        transition={{ type: "spring", damping: 15, stiffness: 200 }}
                        className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                        style={{ backgroundColor: C.green + "15" }}>
                        <Check size={32} style={{ color: C.green }} />
                      </motion.div>
                      <h2 id="auth-modal-title" className="text-xl font-bold"
                        style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                        Neues Passwort ist unterwegs!
                      </h2>
                      <p className="text-sm" style={{ color: C.textMuted }}>
                        Schau in dein E-Mail Postfach (auch im Spam-Ordner)
                      </p>
                      <motion.button whileTap={{ scale: 0.98 }} whileHover={{ scale: 1.02 }}
                        onClick={() => { setMode("login"); setResetSent(false); }}
                        className="w-full h-14 rounded-2xl font-bold text-white transition-shadow hover:shadow-lg"
                        style={{ background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)` }}>
                        Jetzt anmelden
                      </motion.button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <div className="text-center">
                        <h2 id="auth-modal-title" className="text-2xl font-bold"
                          style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                          Passwort vergessen?
                        </h2>
                        <p className="text-sm mt-2" style={{ color: C.textMuted }}>
                          Kein Stress -- wir schicken dir ein neues
                        </p>
                      </div>
                      <InputField icon={Envelope} label="Deine E-Mail" type="email" name="email"
                        placeholder="deine@email.de" value={formData.email} onChange={handleChange} required />
                      <motion.button type="submit" disabled={loading} whileTap={{ scale: 0.98 }} whileHover={{ scale: 1.02 }}
                        className="w-full h-14 rounded-2xl font-bold text-white flex items-center justify-center gap-2 transition-shadow hover:shadow-lg"
                        style={{
                          background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
                          opacity: loading ? 0.6 : 1,
                        }}>
                        {loading ? <Spinner /> : "Neues Passwort anfordern"}
                      </motion.button>
                      <button type="button" onClick={() => setMode("login")}
                        className="w-full text-center text-sm flex items-center justify-center gap-1 transition-colors hover:opacity-70"
                        style={{ color: C.textMuted }}>
                        <ArrowLeft size={14} /> Zurück zur Anmeldung
                      </button>
                    </form>
                  )}
                </motion.div>
              )}

              {/* ===== LOGIN / REGISTER ===== */}
              {!isVerify && !isForgot && (
                <motion.div key={mode} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>

                  {/* Logo */}
                  <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="flex justify-center mb-5">
                    <img
                      src="/logo-frankysfarm.png"
                      alt="Franky's Farm"
                      className="w-24 h-24 object-contain rounded-2xl"
                      style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}
                    />
                  </motion.div>

                  <div className="text-center mb-6">
                    <h2 id="auth-modal-title" className="text-[24px] font-bold"
                      style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                      {isLogin ? "Schoen, dass du da bist!" : "Werde Teil der Familie"}
                    </h2>
                    <p className="text-sm mt-1.5" style={{ color: C.textMuted, fontFamily: BRAND.typography.body }}>
                      {isLogin ? "Dein Matcha wartet schon" : "Sichere dir deinen Willkommens-Gutschein"}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {!isLogin && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="space-y-4 overflow-hidden">
                        <InputField icon={User} label="Dein Name" type="text" name="name"
                          placeholder="Wie heisst du?" value={formData.name} onChange={handleChange} required />
                        <InputField icon={Phone} label="Telefon (fuer Reservierungen, optional)" type="tel" name="phone"
                          placeholder="+49 123 456789" value={formData.phone} onChange={handleChange} />
                      </motion.div>
                    )}

                    <InputField icon={Envelope} label="E-Mail" type="email" name="email"
                      placeholder="deine@email.de" value={formData.email} onChange={handleChange} required />

                    <InputField icon={Lock} label="Passwort" showToggle name="password"
                      placeholder={isLogin ? "Dein Passwort" : "Mindestens 6 Zeichen"}
                      value={formData.password} onChange={handleChange} required minLength={6} />

                    {isLogin && (
                      <div className="text-right">
                        <button type="button" onClick={() => { setMode("forgot"); setResetSent(false); }}
                          className="text-sm font-medium transition-colors hover:opacity-70" style={{ color: C.green }}>
                          Passwort vergessen?
                        </button>
                      </div>
                    )}

                    {/* Submit */}
                    <motion.button type="submit" disabled={loading}
                      whileTap={{ scale: 0.98 }} whileHover={{ scale: 1.02 }}
                      className="w-full h-14 rounded-2xl font-bold text-white text-base flex items-center justify-center gap-2 mt-3 transition-shadow hover:shadow-lg"
                      style={{
                        background: isLogin
                          ? `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`
                          : `linear-gradient(135deg, ${C.orange} 0%, ${C.orangeDark} 100%)`,
                        boxShadow: `0 6px 20px ${isLogin ? C.green : C.orange}25`,
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}>
                      {loading ? <Spinner /> : (
                        <>{isLogin ? "Anmelden" : "Kostenlos registrieren"} <ArrowRight size={18} /></>
                      )}
                    </motion.button>

                    {/* Switch */}
                    <div className="text-center pt-4 pb-2">
                      <span className="text-sm" style={{ color: C.textMuted }}>
                        {isLogin ? "Noch nicht dabei? " : "Schon Mitglied? "}
                      </span>
                      <button type="button"
                        onClick={() => setMode(isLogin ? "register" : "login")}
                        className="text-sm font-bold transition-colors hover:opacity-80"
                        style={{ color: isLogin ? C.orange : C.green }}>
                        {isLogin ? "Jetzt registrieren" : "Anmelden"}
                      </button>
                    </div>

                    {/* Benefits - Register */}
                    {!isLogin && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="p-4 rounded-2xl" style={{ backgroundColor: C.bg }}>
                        <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: C.green }}>
                          Das bekommst du als Mitglied
                        </p>
                        <div className="space-y-3">
                          {[
                            { icon: Gift, text: "Gratis Willkommens-Gutschein" },
                            { icon: Leaf, text: "Punkte sammeln bei jedem Besuch" },
                            { icon: Lightning, text: "Exklusive Angebote nur fuer dich" },
                          ].map((b, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                                style={{ backgroundColor: C.green + "15" }}>
                                <b.icon size={18} weight="fill" style={{ color: C.green }} />
                              </div>
                              <span className="text-sm font-medium" style={{ color: C.text }}>{b.text}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const Spinner = () => (
  <motion.div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
    animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
);
